import {
  shouldIncludeAttachment,
  useGetAttachmentsByDocId,
  type Attachment as ApiAttachment,
} from "@/api/attachment";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ACCORDION_TITLE_CLASS } from "@/lib/accordion-content";
import { computeFieldCollectorNameForOverview } from "@/lib/computeFields";
import { SupportedLocale } from "@/i18n";
import {
  Attachment,
  Multipleprint,
  Multiplesample,
  PublicationStatus,
} from "@/lib/types";
import { ExemplaireThumbnail } from "@/pages/dashboard/shared/ExemplaireThumbnail";
import { Fragment, useMemo } from "react";
import { ExemplaireDrawer } from "./ExemplaireDrawer";

const PUBLISHED_STATUS = "published";

type ExemplaireForDrawer = Parameters<typeof ExemplaireDrawer>[0]["exemplaire"];

interface ExemplairesAccordionProps {
  data: Multipleprint;
  exemplaires: Multiplesample[];
  locale: SupportedLocale;
  t: (key: string) => string;
}

/** Format: N° enregistrement (numérotation) / Type / Date de création / Collection (1 ligne chacun) */
function buildExemplaireSummaryParts(
  exemplaire: ExemplaireForDrawer,
  opts: { locale: SupportedLocale; t: (key: string) => string },
): string[] {
  const parts: string[] = [];
  const nReg =
    exemplaire.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement;
  const num = exemplaire.exemplaireDansEstampesMultiplesDescriptionNumero;
  if (nReg) {
    parts.push(num ? `${nReg} (${num})` : nReg);
  } else if (num) {
    parts.push(num);
  }
  if (exemplaire.exemplaireDansEstampesMultiplesDescriptionType) {
    parts.push(exemplaire.exemplaireDansEstampesMultiplesDescriptionType);
  }
  if (exemplaire.exemplaireDansEstampesMultiplesDescriptionDateTirage) {
    parts.push(exemplaire.exemplaireDansEstampesMultiplesDescriptionDateTirage);
  }
  if (exemplaire.collector) {
    const collectorLabel = computeFieldCollectorNameForOverview(
      {
        collectionneursDescriptionNom:
          exemplaire.collector.collectionneursDescriptionNom ?? "",
        collectionneursDescriptionPrenom:
          exemplaire.collector.collectionneursDescriptionPrenom ?? "",
        collectionneursDescriptionCollectionPublique:
          exemplaire.collector.collectionneursDescriptionCollectionPublique ??
          false,
        collectionneursDescriptionVille:
          exemplaire.collector.collectionneursDescriptionVille,
        collectionneursDescriptionVillePublished:
          exemplaire.collector.collectionneursDescriptionVillePublished,
        collectionneursDescriptionPays:
          exemplaire.collector.collectionneursDescriptionPays,
        collectionneursDescriptionPaysPublished:
          exemplaire.collector.collectionneursDescriptionPaysPublished,
        showCity: true,
      },
      { language: opts.locale, translation: opts.t },
    );
    if (collectorLabel.trim()) parts.push(collectorLabel.trim());
  }
  return parts.length > 0 ? parts : [exemplaire.id];
}

interface ExemplaireRowProps {
  exemplaire: ExemplaireForDrawer;
  multipleprint: Multipleprint;
  multipleprintAttachments: ApiAttachment[] | undefined;
  locale: SupportedLocale;
  t: (key: string) => string;
}

function ExemplaireRow({
  exemplaire,
  multipleprint,
  multipleprintAttachments,
  locale,
  t,
}: ExemplaireRowProps) {
  const parts = buildExemplaireSummaryParts(exemplaire, { locale, t });
  const alt = parts.join(" – ");

  // Prefer attachments already on the sample; otherwise one fetch for the thumbnail.
  const listExemplaireAttachments = (
    exemplaire as ExemplaireForDrawer & { attachments?: ApiAttachment[] }
  ).attachments;
  const { data: fetchedExemplaireAttachments } = useGetAttachmentsByDocId(
    listExemplaireAttachments?.length ? undefined : exemplaire.id,
  );
  const exemplaireAttachments = listExemplaireAttachments?.length
    ? listExemplaireAttachments
    : fetchedExemplaireAttachments;

  const exemplairePublicationStatus =
    (exemplaire.exemplaireDansEstampesMultiplesDescriptionStatus ??
      "draft") as PublicationStatus;
  const multipleprintPublicationStatus =
    (multipleprint.estampesMultiplesPublicationStatus ??
      "draft") as PublicationStatus;

  const filteredExemplaireAttachments = useMemo(
    () =>
      (exemplaireAttachments?.filter((a) =>
        shouldIncludeAttachment(a, exemplairePublicationStatus),
      ) ?? []) as Attachment[],
    [exemplaireAttachments, exemplairePublicationStatus],
  );
  const filteredMultipleprintAttachments = useMemo(
    () =>
      (multipleprintAttachments?.filter((a) =>
        shouldIncludeAttachment(a, multipleprintPublicationStatus),
      ) ?? []) as Attachment[],
    [multipleprintAttachments, multipleprintPublicationStatus],
  );

  // Photo de face de l'exemplaire, sinon repli sur celle de l'estampe parente.
  const thumbnailAttachments = filteredExemplaireAttachments.length
    ? filteredExemplaireAttachments
    : filteredMultipleprintAttachments;
  const thumbnailType = filteredExemplaireAttachments.length
    ? "multiplesample"
    : "multipleprint";

  return (
    <div className="flex items-start gap-3 w-full">
      <ExemplaireThumbnail
        attachments={thumbnailAttachments}
        type={thumbnailType}
        alt={alt}
      />
      <ExemplaireDrawer
        exemplaire={exemplaire}
        multipleprint={multipleprint}
        locale={locale}
        t={t}
        preloadedExemplaireAttachments={exemplaireAttachments}
        preloadedMultipleprintAttachments={multipleprintAttachments}
        trigger={
          <div className="flex-1 min-w-0 cursor-pointer hover:bg-gray-50 transition-colors rounded p-2 -m-2">
            <div className="font-normal text-sm leading-[130%] subpixel-antialiased font-sans line-clamp-4">
              {parts.map((part, i) => (
                <Fragment key={i}>
                  {i > 0 && <br />}
                  {part}
                </Fragment>
              ))}
            </div>
          </div>
        }
      />
    </div>
  );
}

export function ExemplairesAccordion({
  data,
  exemplaires,
  locale,
  t,
}: ExemplairesAccordionProps) {
  const published = (exemplaires ?? []).filter(
    (e) =>
      !e.exemplaireDansEstampesMultiplesDescriptionStatus ||
      e.exemplaireDansEstampesMultiplesDescriptionStatus === PUBLISHED_STATUS,
  );

  // Parent attachments once for the whole accordion (not once per row).
  const listParentAttachments = data.attachments as ApiAttachment[] | undefined;
  const { data: fetchedParentAttachments } = useGetAttachmentsByDocId(
    published.length > 0 && !listParentAttachments?.length
      ? data.id
      : undefined,
  );
  const multipleprintAttachments = listParentAttachments?.length
    ? listParentAttachments
    : fetchedParentAttachments;

  if (published.length === 0) return null;

  return (
    <AccordionItem value="exemplaires">
      <AccordionTrigger className={ACCORDION_TITLE_CLASS}>
        {t("singleMultipleprintSubtitleExemplaires")}
      </AccordionTrigger>
      <AccordionContent className="flex flex-col gap-4 w-full">
        {published.map((exemplaire) => (
          <ExemplaireRow
            key={exemplaire.id}
            exemplaire={exemplaire as ExemplaireForDrawer}
            multipleprint={data}
            multipleprintAttachments={multipleprintAttachments}
            locale={locale}
            t={t}
          />
        ))}
      </AccordionContent>
    </AccordionItem>
  );
}
